import React, { useState, useEffect } from 'react';
import { Activity, ChevronDown, ChevronRight, Megaphone, RefreshCw } from 'lucide-react';
import { analyticsAPI } from '../../../config/analytics-api';
import './Analytics.css';

const WEB_DEVICE = 'web';

const Analytics = () => {
  const [actionLogsTimeFrame, setActionLogsTimeFrame] = useState('5mins');
  const [actionLogsData, setActionLogsData] = useState([]);
  const [promoActionLogs, setPromoActionLogs] = useState([]);
  const [expandedPromos, setExpandedPromos] = useState({});
  const [actionLogsLoading, setActionLogsLoading] = useState(false);

  useEffect(() => {
    fetchActionLogsCount();
  }, [actionLogsTimeFrame]);

  const fetchActionLogsCount = async () => {
    try {
      setActionLogsLoading(true);
      const response = await analyticsAPI.getUserActionLogsCount(actionLogsTimeFrame, WEB_DEVICE);

      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          setActionLogsData(response.data);
          setPromoActionLogs([]);
        } else {
          setActionLogsData(response.data.total || []);
          setPromoActionLogs(response.data.byPromo || []);
        }
        setExpandedPromos({});
      } else {
        setActionLogsData([]);
        setPromoActionLogs([]);
      }
    } catch (error) {
      console.error('Error fetching action logs count:', error);
      setActionLogsData([]);
      setPromoActionLogs([]);
    } finally {
      setActionLogsLoading(false);
    }
  };

  const formatActionType = (type) => {
    return String(type || '')
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const togglePromo = (promo) => {
    setExpandedPromos((prev) => ({ ...prev, [promo]: !prev[promo] }));
  };

  return (
    <div className="analytics-page">
      <div className="analytics-page-header">
        <div className="analytics-page-title">
          <h1>User Analytics</h1>
          <p>Web action logs, including promo / ad attribution</p>
        </div>
        <button type="button" className="analytics-refresh-button" onClick={fetchActionLogsCount}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="analytics-section">
        <h2 className="analytics-section-title">
          <Activity size={20} />
          User Action Logs
        </h2>
        <div className="analytics-filters-container">
          <div className="analytics-time-frame-selector">
            {['5mins', '10mins', '30mins', '1hour'].map((frame) => (
              <button
                key={frame}
                type="button"
                className={`time-frame-button ${actionLogsTimeFrame === frame ? 'active' : ''}`}
                onClick={() => setActionLogsTimeFrame(frame)}
              >
                {frame === '1hour' ? '1 hour' : frame.replace('mins', ' mins')}
              </button>
            ))}
          </div>
        </div>

        {actionLogsLoading ? (
          <div className="analytics-loading-state">
            <div className="analytics-loading-spinner" />
            <p>Loading action logs...</p>
          </div>
        ) : actionLogsData.length === 0 && promoActionLogs.length === 0 ? (
          <div className="analytics-empty-state">
            <p>No user action logs found</p>
          </div>
        ) : (
          <>
            {actionLogsData.length > 0 && (
              <>
                <h3 className="analytics-subsection-title">All Actions</h3>
                <div className="action-logs-grid">
                  {actionLogsData.map((log, index) => (
                    <div key={`${log._id}-${index}`} className="action-log-card">
                      <div className="action-log-label">{formatActionType(log._id)}</div>
                      <div className="action-log-count">{log.count}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {promoActionLogs.length > 0 && (
              <div className="promo-actions-section">
                <h3 className="analytics-subsection-title">
                  <Megaphone size={16} />
                  Promo / Ad Actions
                </h3>
                <div className="promo-actions-list">
                  {promoActionLogs.map((promoItem) => {
                    const isExpanded = !!expandedPromos[promoItem.promo];
                    return (
                      <div key={promoItem.promo} className="promo-action-card">
                        <button
                          type="button"
                          className="promo-action-header"
                          onClick={() => togglePromo(promoItem.promo)}
                        >
                          <span className="promo-action-header-left">
                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            <span className="promo-action-name">{promoItem.promo}</span>
                          </span>
                          <span className="promo-action-total">{promoItem.totalActions} actions</span>
                        </button>
                        {isExpanded && (
                          <div className="action-logs-grid promo-action-grid">
                            {(promoItem.actions || []).map((log, index) => (
                              <div key={`${promoItem.promo}-${log._id}-${index}`} className="action-log-card">
                                <div className="action-log-label">{formatActionType(log._id)}</div>
                                <div className="action-log-count">{log.count}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
